import React, { useEffect, useState } from 'react';
import {
  Typography,
  Link,
  Card,
  CardContent,
  CardBody,
  CardTitle,
  List,
  ListItem,
  Badge,
  Alert
} from '@one-thd/sui-atomic-components';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import { quotePropType } from '../../utils/propTypes';
import { quoteTitleStyles } from '../../constants/responsiveClassNames';

const QuotesDetailsBox = ({ quote }) => {

  const endDate = new Date(quote.expiryDt);
  const quoteStatus = quote.status?.toLowerCase();
  const expiredDateFormatted = endDate.toLocaleDateString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  }); // MM/DD/YY
  const millisecondsInADay = 24 * 60 * 60 * 1000; // One day in milliseconds

  const [timeLeftMs, setTimeLeftMs] = useState(endDate - new Date());
  const [isClient, setIsClient] = useState(false);
  const showCountdown = timeLeftMs > 0 && timeLeftMs < millisecondsInADay;

  useEffect(() => {
    setIsClient(true);
    const interval = setInterval(() => {
      setTimeLeftMs(endDate - new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [endDate]);

  const countDownTimer = (
    <CountdownTimer
      endDate={quote.expiryDt}
      timebombThreshold={millisecondsInADay}
      shouldUseClockDisplay
    />
  );

  return (
    <Card className="sui-w-full" data-testid="quote-details-box">
      <CardContent disablePadding disableGutters>
        <div className="sui-flex sui-justify-between sui-items-center sui-mb-2">
          <Badge
            variant="subtle"
            color={quoteStatus === 'active' ? 'info' : 'primary'}
            size="small"
          >
            {quoteStatus === 'active' ? 'Active' : 'Expired'}
          </Badge>
          {/* here we can add share or refresh link */}
        </div>
        <CardTitle
          header={(
            <Typography lineClamp="2" decoration="underline" component="h3" height="normal">
              <Link
                href={quote.href}
                target="_blank"
                color="primary"
                className={quoteTitleStyles}
              >
                {quote.name}
              </Link>
            </Typography>
          )}
        />
      </CardContent>
      <CardBody>
        <div data-testid="quote-expiration" className="sui-mb-2">
          {showCountdown && isClient ? (
            <Alert status="warning">
              <Typography variant="body-base" component="span">
                <span className="sui-flex sui-items-center sui-gap-1 *:sui-font-normal">
                  Expires in {countDownTimer}
                </span>
              </Typography>
            </Alert>
          ) : (
            <Typography variant="body-base" component="span">
              {quoteStatus === 'active'
                ? `Expires on ${expiredDateFormatted}`
                : `Expired on ${expiredDateFormatted}`}
            </Typography>
          )}
        </div>
        <List className="sui-flex sui-flex-col sui-gap-2">
          <ListItem className="sui-flex sui-gap-2">
            <Typography variant="body-base" weight="bold" component="span">
              <span className="sui-text-nowrap">PO/Job Name:</span>
            </Typography>
            <Typography truncate variant="body-base">{quote.poJobName}</Typography>
          </ListItem>
          <ListItem className="sui-gap-2 sui-hidden @3xl:sui-flex">
            <Typography variant="body-base" weight="bold" component="span">
              <span className="sui-text-nowrap">Subtotal:</span>
            </Typography>
            <Typography truncate variant="body-base">
              ${(quote.subTotal ?? 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </Typography>
          </ListItem>
        </List>
      </CardBody>
    </Card>
  );
};

QuotesDetailsBox.propTypes = {
  quote: quotePropType
};

QuotesDetailsBox.defaultProps = {
  quote: {}
};

export default QuotesDetailsBox;